import { create } from "zustand";

export type Theme = "dark" | "light" | "system";

const STORAGE_KEY = "getpet-admin-theme";

const systemTheme = (): "dark" | "light" =>
  window.matchMedia("(prefers-color-scheme: light)").matches ? "light" : "dark";

const resolve = (theme: Theme): "dark" | "light" =>
  theme === "system" ? systemTheme() : theme;

const apply = (theme: Theme) => {
  document.documentElement.setAttribute("data-theme", resolve(theme));
};

interface ThemeState {
  theme: Theme;
  resolved: "dark" | "light";
  setTheme: (theme: Theme) => void;
  toggle: () => void;
}

const stored = (): Theme => {
  try {
    const value = localStorage.getItem(STORAGE_KEY);
    return value === "light" || value === "dark" || value === "system"
      ? value
      : "dark"; // dark is the default
  } catch {
    return "dark";
  }
};

const initial = stored();

export const useThemeStore = create<ThemeState>((set, get) => ({
  theme: initial,
  resolved: resolve(initial),

  setTheme(theme) {
    try {
      localStorage.setItem(STORAGE_KEY, theme);
    } catch {
      /* private browsing */
    }
    apply(theme);
    set({ theme, resolved: resolve(theme) });
  },

  toggle() {
    // Toggling from "system" lands on the opposite of what is showing, which is
    // what someone reaching for a toggle actually wants.
    const next = get().resolved === "dark" ? "light" : "dark";
    get().setTheme(next);
  },
}));

// Follow the OS while in "system" mode.
window
  .matchMedia("(prefers-color-scheme: light)")
  .addEventListener("change", () => {
    const { theme, setTheme } = useThemeStore.getState();
    if (theme === "system") setTheme("system");
  });

apply(initial);
